import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop", "container", "dialog"]
  static values = {
    open: { type: Boolean, default: false },
    portal: { type: Boolean, default: false }
  }

  connect() {
    this.handleKeyDown = this.handleKeyDown.bind(this)
    this.previousActiveElement = null

    // Cache references before portaling (moving to body removes them from controller scope)
    this._backdrop = this.backdropTarget
    this._container = this.containerTarget
    this._dialog = this.dialogTarget

    // Move backdrop and container to body when portaled or nested inside another modal
    this._portaled = this.portalValue || this.element.closest('[data-controller="modal"]') !== this.element
    if (this._portaled) {
      document.body.appendChild(this._backdrop)
      document.body.appendChild(this._container)
      this._bindPortaledActions()
    }

    if (this.openValue) {
      this.show()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeyDown)
    document.body.classList.remove("overflow-hidden")

    // Clean up portaled elements from body
    if (this._portaled) {
      if (this._backdrop) this._backdrop.remove()
      if (this._container) this._container.remove()
    }

    this._backdrop = null
    this._container = null
    this._dialog = null
  }

  openValueChanged() {
    if (this.openValue) {
      this.show()
    } else {
      this.hide()
    }
  }

  open() {
    this.openValue = true
  }

  close() {
    const wasOpen = this.openValue
    this.openValue = false
    if (wasOpen) this.dispatch("closed")
  }

  toggle() {
    this.openValue = !this.openValue
  }

  show() {
    if (!this._backdrop || !this._container || !this._dialog) return

    // Store currently focused element to restore later
    this.previousActiveElement = document.activeElement

    // Show backdrop and container with animation
    this._backdrop.classList.remove("hidden")
    this._container.classList.remove("hidden")

    // Trigger reflow for animation
    void this._backdrop.offsetWidth

    // Add animation classes
    this._backdrop.classList.remove("opacity-0")
    this._backdrop.classList.add("opacity-100")

    this._dialog.classList.remove("opacity-0", "scale-95")
    this._dialog.classList.add("opacity-100", "scale-100")

    // Prevent body scroll
    document.body.classList.add("overflow-hidden")

    // Add keyboard listener
    document.addEventListener("keydown", this.handleKeyDown)

    // Focus first focusable element after animation
    requestAnimationFrame(() => {
      this.focusFirstElement()
    })
  }

  hide() {
    if (!this._backdrop || !this._container) return

    // Animate out
    this._backdrop.classList.remove("opacity-100")
    this._backdrop.classList.add("opacity-0")

    if (this._dialog) {
      this._dialog.classList.remove("opacity-100", "scale-100")
      this._dialog.classList.add("opacity-0", "scale-95")
    }

    // Wait for animation to complete before hiding
    setTimeout(() => {
      if (this._backdrop) this._backdrop.classList.add("hidden")
      if (this._container) this._container.classList.add("hidden")
    }, 150)

    // Restore body scroll
    document.body.classList.remove("overflow-hidden")

    // Remove keyboard listener
    document.removeEventListener("keydown", this.handleKeyDown)

    // Return focus to previously focused element
    if (this.previousActiveElement && this.previousActiveElement.focus) {
      this.previousActiveElement.focus()
    }
  }

  handleKeyDown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  trapFocus(event) {
    const focusableElements = this.getFocusableElements()

    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey) {
      // Shift + Tab: move backwards
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      }
    } else {
      // Tab: move forwards
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  }

  getFocusableElements() {
    if (!this._dialog) return []

    const selector = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ')

    return Array.from(this._dialog.querySelectorAll(selector))
      .filter(el => el.offsetParent !== null) // Only visible elements
  }

  focusFirstElement() {
    const focusableElements = this.getFocusableElements()

    if (focusableElements.length > 0) {
      // Focus the first non-close button element, or close button if that's all there is
      const firstInteractive = focusableElements.find(el =>
        !el.getAttribute('aria-label')?.includes('Close')
      ) || focusableElements[0]

      firstInteractive.focus()
    }
  }

  // When portaled, Stimulus actions (click->modal#close) no longer work
  // because elements are outside the controller's DOM scope. Manually bind handlers.
  _bindPortaledActions() {
    this._handleBackdropClose = () => this.close()
    this._handleDialogStop = (e) => e.stopPropagation()

    // Backdrop click → close
    this._backdrop.addEventListener("click", this._handleBackdropClose)

    // Dialog click → stop propagation (prevent backdrop close)
    this._dialog.addEventListener("click", this._handleDialogStop)

    // Any element with data-action containing "modal#close" inside the container
    this._container.querySelectorAll('[data-action*="modal#close"]').forEach(el => {
      el.addEventListener("click", this._handleBackdropClose)
    })
  }

  // Prevent closing when clicking inside dialog
  stopPropagation(event) {
    event.stopPropagation()
  }
}
